import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, onUnmounted, computed } from 'vue';
import videojs, { VideoJsPlayer } from 'video.js';
import rvtt from 'videojs-vtt-thumbnails'; // Import the VTT thumbnails plugin
import 'videojs-quality-selector-hls';
import chromecast from '@silvermine/videojs-chromecast';
import airPlay from '@silvermine/videojs-airplay';

import Player from 'video.js/dist/types/player';
import 'videojs-mux';
import { IVideoMetadata } from '~/types/mux-player';

type TDecoratedPlayer = Player & VideoJsPlayer;


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoPlayerRawDCDN',
  props: {
    videoId: null,
    poster: null,
    thumbnailVtt: null,
    startTime: null,
    videoMetadata: null
  },
  emits: [
  'play',
  'pause',
  'seeked',
  'ended',
  'canplay',
  'mounted',
],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  videoId: string;
  poster: string;
  thumbnailVtt?: string;
  startTime?: number;
  videoMetadata?: Partial<IVideoMetadata>;
};

chromecast(videojs);
airPlay(videojs);
videojs.registerPlugin('vttThumbnails', rvtt); // Register the VTT thumbnails plugin





const videoPlayerRef = ref<HTMLVideoElement | null>(null);
const player = ref<TDecoratedPlayer | null>(null);

const source = computed(() => `${process.env.filmNodeEgress}/${props.videoId}`);
const thumbnailsVtt =
  props.thumbnailVtt ??
  `${process.env.filmNodeEgress}/${props.videoId}/thumbnails`;

const muxDataEnvKey = process.env.dcdnMuxDataEnvKey;

function initializePlayer() {
  if (videoPlayerRef.value) {
    player.value = videojs(videoPlayerRef.value, {
      techOrder: ['chromecast', 'html5'],
      chromecast: {
        requestTitleFn: function () {
          // Not required
          return props?.videoMetadata?.video_title || 'Gala Film';
        },
        requestSubtitleFn: function () {
          // Not required
          return 'Gala Film - Watch. Unlock. Own.';
        },
      },
      autoplay: true,
      plugins: {
        chromecast: {},
        airPlay: {},
        mux: {
          debug: false,
          data: {
            env_key: muxDataEnvKey,
            ...props.videoMetadata,
            player_name: 'Gala-Film-DCDN-Player',
            player_version: '1.0.0',
            video_id: props.videoId,
          },
        },
      },
      fluid: true,
      html5: {
        vhs: {
          maxBufferLength: 80,
          bufferLowWaterLine: 15,
          bufferHighWaterLine: 30,
          maxBufferAhead: 20,
          maxBufferHole: 0.3,
          initialBandwidth: 5000000,
          bandwidthSwitchUpscaleFactor: 0.5,
          bandwidthSwitchDownscaleFactor: 0.8,
          timeout: 60000,
          retryCount: 5,
          retryDelay: 2000,
          maxSegmentLength: 10,
          enableLowInitialPlaylist: false,
          useBandwidthFromLocalStorage: false,
        },
      },
    }) as TDecoratedPlayer;

    player.value.vttThumbnails({
      src: thumbnailsVtt,
      showTimestamp: false,
    });

    player.value.qualitySelectorHls({
      displayCurrentQuality: true,
    });

    player.value.fill(true);
    player.value.on('play', () => emit('play'));
    player.value.on('pause', () => emit('pause'));
    player.value.on('seeked', () =>
      emit('seeked', player.value!.currentTime()),
    );
    player.value.on('ended', () => emit('ended'));
    player.value.on('canplay', () => emit('canplay'));

    player.value.on('loadedmetadata', function () {
      const videoElement = (player.value as TDecoratedPlayer)
        .tech()
        .el() as HTMLVideoElement;

      // Retrieve the video width and height from the video element
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      if (videoWidth && videoHeight) {
        const aspectRatio = (videoHeight / videoWidth) * 100;
        const container = document.querySelector(
          '.video-container',
        ) as HTMLElement;
        container.style.paddingTop = `${aspectRatio}%`; // Adjust the padding to maintain the aspect ratio
      }
    });
  }
}

function getVideoDuration() {
  if (player.value) {
    return player.value.duration();
  }
  return 0;
}

function getVideoCurrentTime() {
  if (player.value) {
    return player.value.currentTime();
  }
  return 0;
}

function isPaused() {
  if (player.value) {
    return player.value.paused();
  }
  return true;
}

function play() {
  player.value?.play();
}

function pause() {
  player.value?.pause();
}

function setCurrentTime(time: number) {
  if (player.value) {
    player.value.currentTime(time);
  }
}

expose({
  getVideoDuration,
  getVideoCurrentTime,
  isPaused,
  play,
  pause,
  setCurrentTime,
});

onMounted(() => {
  initializePlayer();
  emit('mounted');
});

onUnmounted(() => {
  if (player.value) {
    player.value.off('play');
    player.value.off('pause');
    player.value.off('seeked');
    player.value.off('ended');
    player.value.dispose();
  }
});

return { __sfc: true,props, emit, videoPlayerRef, player, source, thumbnailsVtt, muxDataEnvKey, initializePlayer, getVideoDuration, getVideoCurrentTime, isPaused, play, pause, setCurrentTime }
}

})